import { useState, useCallback, useMemo } from 'react'
import { Popover, Input } from 'antd'
import { SearchOutlined, SmileOutlined } from '@ant-design/icons'
import styles from './styles.module.less'

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void
  children: React.ReactNode
}

// Danh sách emoji theo categories giống Facebook
const EMOJI_CATEGORIES = {
  'Phổ biến': ['😀', '😂', '😍', '🥰', '😊', '😭', '😘', '😎', '🤔', '😏', '😴', '😌', '🙄', '🤗', '😱', '😳', '😤', '😠', '😡', '🤬', '😮', '😯', '😲', '😵', '🤯', '😶', '😐', '😑', '😬', '🤐'],
  'Khuôn mặt': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔'],
  'Động vật': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐽', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉', '🦇'],
  'Ẩm thực': ['🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🌽', '🥕', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨'],
  'Hoạt động': ['⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸', '🥅', '🏒', '🏑', '🏏', '🥊', '🥋', '🎽', '⛸️', '🥌', '🎿', '🏂', '🏄', '🏊', '🤽', '🏋️', '🚴', '🚵', '🤸', '🤾', '🤹'],
  'Du lịch': ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🛴', '🚲', '🛵', '🏍️', '🚨', '🚔', '🚍', '✈️', '🛫', '🛬', '🛩️', '💺', '🚁', '🚟', '🚠', '🚡', '🛰️'],
  'Đồ vật': ['💡', '🔦', '🕯️', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🔩', '⚙️', '🗜️', '⛓️', '🔫', '💣', '🔪', '🗡️', '⚔️', '🛡️', '🚬'],
  'Biểu tượng': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐'],
}

const CATEGORY_ICONS: Record<string, string> = {
  'Phổ biến': '😀',
  'Khuôn mặt': '😊',
  'Động vật': '🐶',
  'Ẩm thực': '🍎',
  'Hoạt động': '⚽',
  'Du lịch': '🚗',
  'Đồ vật': '💡',
  'Biểu tượng': '❤️',
}

const CATEGORY_KEYS = Object.keys(EMOJI_CATEGORIES)

// Mapping emoji sang tên tiếng Việt để tìm kiếm
const EMOJI_NAMES: Record<string, string[]> = {
  '😀': ['cười', 'vui', 'hạnh phúc', 'mặt cười', 'smile', 'happy'],
  '😂': ['cười', 'cười nước mắt', 'vui', 'haha', 'laugh'],
  '😍': ['yêu', 'thích', 'đẹp', 'tim mắt', 'love', 'heart eyes'],
  '🥰': ['yêu', 'hạnh phúc', 'tim', 'love', 'happy'],
  '😊': ['cười', 'vui', 'hạnh phúc', 'smile'],
  '😭': ['khóc', 'buồn', 'cry', 'sad', 'tears'],
  '😘': ['hôn', 'yêu', 'kiss', 'love'],
  '😎': ['ngầu', 'cool', 'kính râm', 'sunglasses'],
  '🤔': ['suy nghĩ', 'nghĩ', 'think', 'thinking'],
  '😏': ['cười', 'mỉm cười', 'smirk'],
  '😴': ['ngủ', 'sleep', 'buồn ngủ'],
  '😌': ['thư giãn', 'relax', 'nhẹ nhàng'],
  '🙄': ['mắt lộn', 'eye roll', 'không tin'],
  '🤗': ['ôm', 'hug', 'ôm ấp'],
  '😱': ['sợ', 'hoảng sợ', 'shock', 'surprised'],
  '😳': ['xấu hổ', 'ngượng', 'blush', 'embarrassed'],
  '😤': ['tức', 'giận', 'angry'],
  '😠': ['giận', 'tức', 'angry', 'mad'],
  '😡': ['rất giận', 'tức giận', 'very angry'],
  '🤬': ['chửi', 'tức', 'swear'],
  '😮': ['ngạc nhiên', 'surprised', 'oh'],
  '😯': ['ngạc nhiên', 'surprised'],
  '😲': ['ngạc nhiên', 'wow', 'surprised'],
  '😵': ['chóng mặt', 'dizzy'],
  '🤯': ['nổ', 'mind blown', 'explode'],
  '😶': ['im lặng', 'silent'],
  '😐': ['bình thường', 'neutral'],
  '😑': ['không cảm xúc', 'expressionless'],
  '😬': ['căng thẳng', 'nervous'],
  '🤐': ['im miệng', 'zipper mouth'],
  '😃': ['cười lớn', 'big smile'],
  '😄': ['cười', 'smile'],
  '😁': ['cười', 'grin'],
  '😆': ['cười lớn', 'laugh'],
  '😅': ['cười', 'nervous laugh'],
  '🤣': ['cười', 'rolling laugh'],
  '🙂': ['cười nhẹ', 'slight smile'],
  '🙃': ['ngược', 'upside down'],
  '😉': ['nháy mắt', 'wink'],
  '😇': ['thiên thần', 'angel', 'halo'],
  '🤩': ['sao mắt', 'star eyes'],
  '😗': ['hôn', 'kiss'],
  '😚': ['hôn', 'kiss'],
  '😙': ['hôn', 'kiss'],
  '😋': ['ngon', 'yummy', 'delicious'],
  '😛': ['lè lưỡi', 'tongue'],
  '😜': ['nháy mắt lè lưỡi', 'wink tongue'],
  '🤪': ['điên', 'crazy', 'zany'],
  '😝': ['lè lưỡi', 'tongue'],
  '🤑': ['tiền', 'money', 'dollar'],
  '🤭': ['giấu', 'hand over mouth'],
  '🤫': ['im lặng', 'shush'],
  '🐶': ['chó', 'dog', 'cún'],
  '🐱': ['mèo', 'cat'],
  '🐭': ['chuột', 'mouse'],
  '🐹': ['hamster', 'chuột hamster'],
  '🐰': ['thỏ', 'rabbit', 'bunny'],
  '🦊': ['cáo', 'fox'],
  '🐻': ['gấu', 'bear'],
  '🐼': ['gấu trúc', 'panda'],
  '🐨': ['gấu koala', 'koala'],
  '🐯': ['hổ', 'tiger'],
  '🦁': ['sư tử', 'lion'],
  '🐮': ['bò', 'cow'],
  '🐷': ['heo', 'pig'],
  '🐽': ['mũi heo', 'pig nose'],
  '🐸': ['ếch', 'frog'],
  '🐵': ['khỉ', 'monkey'],
  '🙈': ['khỉ che mắt', 'see no evil'],
  '🙉': ['khỉ che tai', 'hear no evil'],
  '🙊': ['khỉ che miệng', 'speak no evil'],
  '🐒': ['khỉ', 'monkey'],
  '🐔': ['gà', 'chicken'],
  '🐧': ['chim cánh cụt', 'penguin'],
  '🐦': ['chim', 'bird'],
  '🐤': ['gà con', 'baby chick'],
  '🐣': ['gà nở', 'hatching chick'],
  '🐥': ['gà con', 'front facing chick'],
  '🦆': ['vịt', 'duck'],
  '🦅': ['đại bàng', 'eagle'],
  '🦉': ['cú', 'owl'],
  '🦇': ['dơi', 'bat'],
  '🍎': ['táo', 'apple'],
  '🍊': ['cam', 'orange'],
  '🍋': ['chanh', 'lemon'],
  '🍌': ['chuối', 'banana'],
  '🍉': ['dưa hấu', 'watermelon'],
  '🍇': ['nho', 'grapes'],
  '🍓': ['dâu', 'strawberry'],
  '🍈': ['dưa', 'melon'],
  '🍒': ['anh đào', 'cherry'],
  '🍑': ['đào', 'peach'],
  '🥭': ['xoài', 'mango'],
  '🍍': ['dứa', 'pineapple'],
  '🥥': ['dừa', 'coconut'],
  '🥝': ['kiwi', 'kiwi fruit'],
  '🍅': ['cà chua', 'tomato'],
  '🍆': ['cà tím', 'eggplant'],
  '🥑': ['bơ', 'avocado'],
  '🥦': ['bông cải', 'broccoli'],
  '🥬': ['rau', 'leafy green'],
  '🥒': ['dưa chuột', 'cucumber'],
  '🌶️': ['ớt', 'pepper', 'chili'],
  '🌽': ['ngô', 'corn'],
  '🥕': ['cà rốt', 'carrot'],
  '🥔': ['khoai tây', 'potato'],
  '🍠': ['khoai lang', 'sweet potato'],
  '🥐': ['bánh sừng bò', 'croissant'],
  '🥯': ['bagel', 'bánh bagel'],
  '🍞': ['bánh mì', 'bread'],
  '🥖': ['bánh mì dài', 'baguette'],
  '🥨': ['bánh quy xoắn', 'pretzel'],
  '⚽': ['bóng đá', 'football', 'soccer'],
  '🏀': ['bóng rổ', 'basketball'],
  '🏈': ['bóng bầu dục', 'american football'],
  '⚾': ['bóng chày', 'baseball'],
  '🎾': ['tennis', 'quần vợt'],
  '🏐': ['bóng chuyền', 'volleyball'],
  '🏉': ['bóng bầu dục', 'rugby'],
  '🎱': ['bi-a', 'pool', 'billiards'],
  '🏓': ['bóng bàn', 'ping pong', 'table tennis'],
  '🏸': ['cầu lông', 'badminton'],
  '🥅': ['khung thành', 'goal'],
  '🏒': ['khúc côn cầu', 'hockey'],
  '🏑': ['field hockey'],
  '🏏': ['cricket'],
  '🥊': ['đấm bốc', 'boxing'],
  '🥋': ['võ', 'martial arts'],
  '🎽': ['áo thể thao', 'running shirt'],
  '⛸️': ['trượt băng', 'ice skate'],
  '🥌': ['curling'],
  '🎿': ['trượt tuyết', 'ski'],
  '🏂': ['snowboard'],
  '🏄': ['lướt sóng', 'surfing'],
  '🏊': ['bơi', 'swimming'],
  '🤽': ['water polo'],
  '🏋️': ['cử tạ', 'weight lifting'],
  '🚴': ['đạp xe', 'biking'],
  '🚵': ['mountain biking'],
  '🤸': ['gymnastics'],
  '🤾': ['handball'],
  '🤹': ['juggling'],
  '🚗': ['xe hơi', 'car', 'ô tô'],
  '🚕': ['taxi'],
  '🚙': ['xe SUV', 'SUV'],
  '🚌': ['xe buýt', 'bus'],
  '🚎': ['xe điện', 'trolleybus'],
  '🏎️': ['xe đua', 'racing car'],
  '🚓': ['xe cảnh sát', 'police car'],
  '🚑': ['xe cứu thương', 'ambulance'],
  '🚒': ['xe cứu hỏa', 'fire truck'],
  '🚐': ['xe tải nhỏ', 'minivan'],
  '🚚': ['xe tải', 'truck'],
  '🚛': ['xe tải lớn', 'articulated lorry'],
  '🚜': ['máy kéo', 'tractor'],
  '🛴': ['xe tay ga', 'scooter'],
  '🚲': ['xe đạp', 'bicycle', 'bike'],
  '🛵': ['xe máy', 'motor scooter'],
  '🏍️': ['xe máy', 'motorcycle'],
  '🚨': ['xe cảnh sát', 'police car light'],
  '🚔': ['xe cảnh sát', 'police car'],
  '🚍': ['xe buýt', 'oncoming bus'],
  '✈️': ['máy bay', 'airplane', 'plane'],
  '🛫': ['cất cánh', 'airplane departure'],
  '🛬': ['hạ cánh', 'airplane arrival'],
  '🛩️': ['máy bay nhỏ', 'small airplane'],
  '💺': ['ghế', 'seat'],
  '🚁': ['trực thăng', 'helicopter'],
  '🚟': ['suspension railway'],
  '🚠': ['cáp treo', 'mountain cableway'],
  '🚡': ['aerial tramway'],
  '🛰️': ['vệ tinh', 'satellite'],
  '💡': ['bóng đèn', 'light bulb', 'đèn'],
  '🔦': ['đèn pin', 'flashlight'],
  '🕯️': ['nến', 'candle'],
  '🧯': ['bình chữa cháy', 'fire extinguisher'],
  '🛢️': ['dầu', 'oil'],
  '💸': ['tiền bay', 'money with wings'],
  '💵': ['tiền đô', 'dollar'],
  '💴': ['yen'],
  '💶': ['euro'],
  '💷': ['pound'],
  '💰': ['tiền', 'money bag', 'túi tiền'],
  '💳': ['thẻ tín dụng', 'credit card'],
  '💎': ['kim cương', 'diamond', 'gem'],
  '⚖️': ['cân', 'scales', 'justice'],
  '🔧': ['cờ lê', 'wrench'],
  '🔨': ['búa', 'hammer'],
  '⚒️': ['hammer and pick'],
  '🛠️': ['bộ dụng cụ', 'toolbox'],
  '⛏️': ['cuốc', 'pick'],
  '🔩': ['ốc vít', 'nut and bolt'],
  '⚙️': ['bánh răng', 'gear'],
  '🗜️': ['kẹp', 'clamp'],
  '⛓️': ['xích', 'chains'],
  '🔫': ['súng', 'pistol', 'gun'],
  '💣': ['bom', 'bomb'],
  '🔪': ['dao', 'knife'],
  '🗡️': ['kiếm', 'sword'],
  '⚔️': ['kiếm chéo', 'crossed swords'],
  '🛡️': ['khiên', 'shield'],
  '🚬': ['thuốc lá', 'cigarette'],
  '❤️': ['tim đỏ', 'red heart', 'yêu'],
  '🧡': ['tim cam', 'orange heart'],
  '💛': ['tim vàng', 'yellow heart'],
  '💚': ['tim xanh lá', 'green heart'],
  '💙': ['tim xanh dương', 'blue heart'],
  '💜': ['tim tím', 'purple heart'],
  '🖤': ['tim đen', 'black heart'],
  '🤍': ['tim trắng', 'white heart'],
  '🤎': ['tim nâu', 'brown heart'],
  '💔': ['tim vỡ', 'broken heart', 'buồn'],
  '❣️': ['tim cảm xúc', 'heart exclamation'],
  '💕': ['hai tim', 'two hearts'],
  '💞': ['tim xoay', 'revolving hearts'],
  '💓': ['tim đập', 'beating heart'],
  '💗': ['tim lớn', 'growing heart'],
  '💖': ['tim lấp lánh', 'sparkling heart'],
  '💘': ['tim tên', 'cupid'],
  '💝': ['tim hộp quà', 'heart with ribbon'],
  '💟': ['tim trang trí', 'heart decoration'],
  '☮️': ['hòa bình', 'peace'],
  '✝️': ['thánh giá', 'cross'],
  '☪️': ['sao lưỡi liềm', 'star and crescent'],
  '🕉️': ['om', 'om symbol'],
  '☸️': ['bánh xe pháp', 'wheel of dharma'],
  '✡️': ['sao david', 'star of david'],
  '🔯': ['sao sáu cánh', 'six pointed star'],
  '🕎': ['menorah'],
  '☯️': ['âm dương', 'yin yang'],
  '☦️': ['orthodox cross'],
  '🛐': ['nhà thờ', 'place of worship'],
}

// Mapping từ ký tự mặt cười sang emoji
const EMOTICON_TO_EMOJI: Record<string, string> = {
  ':))': '😂',
  ':)': '😊',
  ':-)': '😊',
  ':D': '😀',
  ':-D': '😀',
  ':(': '😢',
  ':-(': '😢',
  ':((': '😭',
  ':-((': '😭',
  ':P': '😛',
  ':-P': '😛',
  ':p': '😛',
  ':-p': '😛',
  ';)': '😉',
  ';-)': '😉',
  ':O': '😮',
  ':-O': '😮',
  ':o': '😮',
  ':-o': '😮',
  ':*': '😘',
  ':-*': '😘',
  ':|': '😐',
  ':-|': '😐',
  ':/': '😕',
  ':-/': '😕',
  ':\\': '😕',
  ':-\\': '😕',
  ':3': '😊',
  ':-3': '😊',
  '>:(': '😠',
  '>:-(': '😠',
  '>:)': '😈',
  '>:-)': '😈',
  'xD': '😆',
  'XD': '😆',
  'x)': '😆',
  'X)': '😆',
  'T_T': '😭',
  'T.T': '😭',
  '^_^': '😊',
  '^^': '😊',
  'o_O': '😲',
  'O_O': '😲',
  'o.o': '😲',
  'O.O': '😲',
  '-_-': '😑',
  'u_u': '😑',
  'U_U': '😑',
  '<3': '❤️',
  '</3': '💔',
  'B)': '😎',
  'B-)': '😎',
  ':S': '😕',
  ':-S': '😕',
  ':s': '😕',
  ':-s': '😕',
}

// Helper function để filter emoji
const filterEmojis = (searchTerm: string, selectedCategory: string): string[] => {
  const searchLower = searchTerm.toLowerCase().trim()
  
  if (!searchLower) {
    const categoryEmojis = EMOJI_CATEGORIES[selectedCategory as keyof typeof EMOJI_CATEGORIES] || []
    return categoryEmojis
  }

  // Kiểm tra nếu search term là emoticon
  const normalizedSearch = searchTerm.trim()
  if (EMOTICON_TO_EMOJI[normalizedSearch]) {
    return [EMOTICON_TO_EMOJI[normalizedSearch]]
  }

  // Kiểm tra các biến thể của emoticon
  const emoticonVariants = [
    normalizedSearch,
    normalizedSearch.replace(/-/g, ''),
    normalizedSearch.replace(/:/g, ''),
    normalizedSearch.toLowerCase(),
    normalizedSearch.toUpperCase(),
  ]

  for (const variant of emoticonVariants) {
    if (EMOTICON_TO_EMOJI[variant]) {
      return [EMOTICON_TO_EMOJI[variant]]
    }
  }

  // Tìm kiếm trong tất cả emoji bằng tên
  const allEmojis = Object.values(EMOJI_CATEGORIES).flat()
  const uniqueEmojis = Array.from(new Set(allEmojis))
  
  return uniqueEmojis.filter(emoji => {
    const names = EMOJI_NAMES[emoji] || []
    return names.some(name => name.toLowerCase().includes(searchLower))
  })
}

export const EmojiPicker = ({ onEmojiSelect, children }: EmojiPickerProps) => {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('Phổ biến')

  const handleEmojiClick = useCallback((emoji: string) => {
    onEmojiSelect(emoji)
    // Không đóng picker để có thể chọn nhiều emoji
  }, [onEmojiSelect])

  const filteredEmojis = useMemo(
    () => filterEmojis(searchTerm, selectedCategory),
    [selectedCategory, searchTerm]
  )

  const content = (
    <div className={styles.emojiPicker}>
      <div className={styles.emojiPickerHeader}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Tìm kiếm biểu tượng cảm xúc"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.emojiSearch}
          allowClear
        />
      </div>
      
      {!searchTerm && (
        <div className={styles.emojiCategoryLabel}>
          {selectedCategory}
        </div>
      )}

      <div className={styles.emojiGridContainer}>
        <div className={styles.emojiGrid}>
          {filteredEmojis.map((emoji, index) => (
            <button
              key={`${emoji}-${index}`}
              type="button"
              className={styles.emojiButton}
              onClick={() => handleEmojiClick(emoji)}
              title={emoji}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>

      {!searchTerm && (
        <div className={styles.emojiCategoryBar}>
          {CATEGORY_KEYS.map((category) => (
            <button
              key={category}
              type="button"
              className={`${styles.categoryButton} ${selectedCategory === category ? styles.categoryButtonActive : ''}`}
              onClick={() => setSelectedCategory(category)}
              title={category}
            >
              {CATEGORY_ICONS[category] || <SmileOutlined />}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen)
        if (!newOpen) {
          setSearchTerm('')
          setSelectedCategory('Phổ biến')
        }
      }}
      placement="top"
      overlayClassName={styles.emojiPickerOverlay}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      align={{ offset: [0, -8] }}
      styles={{ body: { padding: 0 } }}
    >
      {children}
    </Popover>
  )
}

